import { css, cx } from '@linaria/core'
import i18n from 'i18next'
import { type FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMarkets } from '#api/utils/useMarkets'
import { Button } from '#components/Button'
import { Checkbox } from '#components/Checkbox'
import { Icon } from '#components/Icon'
import { PartPeriodInput } from '#components/PartPeriodInput'
import { Select, type SelectEntry } from '#components/Select'
import { Spinner } from '#components/Spinner'
import { type FullApiConfiguration, isTg3ApiConfiguration, type OverrideableApiConfiguration } from '#features/Configuration/ApiConfiguration'
import { useFilteredMarkets } from '#features/Markets/useFilteredMarkets'
import { translateBestEffort } from '#i18n/LocalizedString'
import ChevronDownIcon from '#icons/chevron-1-down-16.svg'
import ChevronRightIcon from '#icons/chevron-1-right-16.svg'
import ChangeIcon from '#icons/info-circle.svg'
import type { OverrideRecord } from '#pages/Configuration/ChangeBaseParameters/OverrideRecord'
import { ConfigurationTypeInput } from '#pages/TechnicalSetup/ConfigurationTypeInput'
import { LanguageInput } from '#pages/TechnicalSetup/LanguageInput'
import { MarketInput } from '#pages/TechnicalSetup/MarketInput'
import { TextInput } from '#pages/TechnicalSetup/TextInput'
import { VehicleTypeInput } from '#pages/TechnicalSetup/VehicleTypeInput'
import { ViewInput } from '#pages/TechnicalSetup/ViewInput'
import { themeColors } from '#styles/themes'
import type { EnhancedMarket } from '#types/EnhancedMarket'
import {
  type DateString,
  dateToString,
  formatPartPeriod,
  isValidDateString,
  type PartPeriod,
  periodToDate,
  periodToString,
  stringToDate,
  stringToPeriod,
} from '#utils/DateString.ts'
import { ConfigurationTypeLabel, LanguageLabel, MarketLabel, VehicleTypeLabel, ViewLabel } from '#utils/ecmIdLabels'
import { formatDate } from '#utils/formatDateTime'
import { minMaxDate } from '#utils/minMaxDate'

const gridClass = css`
  display: grid;
  grid-template-columns: minmax(min-content, 200px) 350px minmax(200px, min-content);
  column-gap: 16px;
  row-gap: 8px;
  align-items: center;
  padding: 8px 24px;  
  align-self: center;
  overflow-y: auto;

  > ._usage-filter {
    grid-column: 1 / -1;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    padding-bottom: 20px;
  }

  > ._expander {
    grid-column: 1 / -1;
    padding-left: 2px;
    text-align: left;
    display: flex;
    justify-content: flex-start;
    gap: 4px;
  }

  > hr {
    grid-column: 1 / -1;
    border: none;

    margin: 20px 0 12px;
    background:  ${themeColors.lineDefault};
    height: 1px;
  }

  > ._prev-value {
    text-decoration: line-through;
  }
`

export interface OverridesState {
  overrides: OverrideRecord<OverrideableApiConfiguration>
  hasOverrides: boolean
  effectiveConfiguration: Partial<FullApiConfiguration>
}

interface InternalState {
  prevValueLabels: Partial<Record<keyof FullApiConfiguration, string>>
  selectedConfiguration: Partial<FullApiConfiguration>
  overrideTargetMarket: EnhancedMarket | undefined
  effectiveMarket: EnhancedMarket | undefined
  effectiveConfiguration: Partial<FullApiConfiguration>
}

interface Props {
  apiConfiguration: FullApiConfiguration
  overrides: OverridesState
  onOverridesChange?: (state: OverridesState) => void
  disabled?: boolean
  calculatedPartPeriod?: DateString
  displayMode?: 'part-period' | 'full'
}

export const OverridesGrid: FC<Props> = ({
  apiConfiguration: rawApiConfiguration,
  disabled,
  calculatedPartPeriod,
  overrides: { overrides },
  onOverridesChange,
  displayMode = 'full',
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  if (isTg3ApiConfiguration(rawApiConfiguration)) {
    throw new Error('OverridesGrid does not support TG3 configurations')
  }

  const apiConfiguration = rawApiConfiguration satisfies OverrideableApiConfiguration

  const { markets, status: marketStatus } = useMarkets()
  const [isExpanded, setIsExpanded] = useState(false)

  const { filteredMarkets, availableUsages, selectedUsage, setSelectedUsage, showAllUsagesGroup } = useFilteredMarkets(markets)

  const marketOptions = useMemo(() => {
    return filteredMarkets.map((market) => ({
      id: market.countryCode,
      value: `${translateBestEffort(market.label, language, market.languages?.[0].languageCode)} (${market.countryCode})`,
    }))
  }, [filteredMarkets, language])

  const [{ prevValueLabels, selectedConfiguration, effectiveMarket, overrideTargetMarket, effectiveConfiguration }, setInternalState] =
    useState<InternalState>({
      prevValueLabels: {},
      selectedConfiguration: {},
      overrideTargetMarket: undefined,
      effectiveMarket: undefined,
      effectiveConfiguration: apiConfiguration,
    })

  const marketLanguageOptions = useMemo(() => {
    return (
      effectiveMarket?.languages?.map((language) => ({
        id: language.languageCode,
        value: `${translateBestEffort(language.languageLabel, language.languageCode, language.languageCode)} (${language.languageCode})`,
      })) ?? []
    )
  }, [effectiveMarket])

  const usageFilterOptions = useMemo(() => {
    const filterOptions: SelectEntry[] = showAllUsagesGroup ? [{ value: t('setup.preset.all', 'All'), id: '__all__' }] : []
    for (const availableUsage of availableUsages) {
      filterOptions.push({ value: availableUsage, id: availableUsage })
    }
    return filterOptions
  }, [availableUsages, showAllUsagesGroup, t])

  const setOverrides = useCallback(
    (newOverrides: Partial<OverrideRecord<OverrideableApiConfiguration>>) => {
      const mergedOverrides = {
        ...overrides,
        ...newOverrides,
      }

      const baseMarket = markets.find((market) => market.countryCode === apiConfiguration.orderCountry)
      const overrideCountryCode =
        mergedOverrides.orderCountry?.override && mergedOverrides.orderCountry?.value
          ? mergedOverrides.orderCountry?.value
          : apiConfiguration.orderCountry
      const overrideTargetMarket = mergedOverrides.orderCountry?.override
        ? markets.find((market) => market.countryCode === overrideCountryCode)
        : undefined

      const effectiveMarket = overrideTargetMarket ?? baseMarket

      const effectiveConfiguration: Partial<FullApiConfiguration> = {
        ...apiConfiguration,
      }
      const selectedConfiguration: Partial<FullApiConfiguration> = {
        ...apiConfiguration,
      }
      const prevValueLabels: Partial<Record<keyof FullApiConfiguration, string>> = {}

      for (const [key, value] of Object.entries(mergedOverrides)) {
        if (key && value?.value && value.override) {
          // @ts-expect-error sadly we need to use dynamic keys, as Object entries breaks the type linking
          selectedConfiguration[key] = value.value
        }
      }

      if (
        mergedOverrides.orderCountry?.override &&
        mergedOverrides.orderCountry?.value &&
        mergedOverrides.orderCountry.value !== apiConfiguration.orderCountry
      ) {
        prevValueLabels.orderCountry = `${translateBestEffort(
          markets.find((market) => market.countryCode === apiConfiguration.orderCountry)?.label,
          language,
          apiConfiguration.orderCountry,
        )} (${apiConfiguration.orderCountry})`
        effectiveConfiguration.orderCountry = mergedOverrides.orderCountry.value
      }

      const baseLanguage = baseMarket?.languages?.find((language) => language.languageCode === apiConfiguration.orderLanguage)

      if (
        mergedOverrides.orderLanguage?.override &&
        mergedOverrides.orderLanguage?.value &&
        (mergedOverrides.orderLanguage.value !== apiConfiguration.orderLanguage || baseMarket !== overrideTargetMarket)
      ) {
        const targetLanguage = effectiveMarket?.languages?.find((language) => language.languageCode === mergedOverrides.orderLanguage?.value)
        const currentLanguage = baseMarket?.languages?.find((language) => language.languageCode === apiConfiguration.orderLanguage)

        if (targetLanguage?.languageCode !== currentLanguage?.languageCode) {
          prevValueLabels.orderLanguage = `${translateBestEffort(
            baseMarket?.languages?.find((language) => language.languageCode === apiConfiguration.orderLanguage)?.languageLabel,
            language,
            apiConfiguration.orderLanguage,
          )} (${apiConfiguration.orderLanguage})`
          effectiveConfiguration.orderLanguage = mergedOverrides.orderLanguage.value
        } else {
          prevValueLabels.orderLanguage = undefined
          effectiveConfiguration.orderLanguage = apiConfiguration.orderLanguage
        }
      } else if (
        overrideTargetMarket &&
        overrideTargetMarket !== baseMarket &&
        overrideTargetMarket?.languages?.[0]?.languageCode !== apiConfiguration.orderLanguage
      ) {
        selectedConfiguration.orderLanguage = overrideTargetMarket.languages?.[0]?.languageCode
        prevValueLabels.orderLanguage = `${translateBestEffort(
          baseMarket?.languages?.find((language) => language.languageCode === apiConfiguration.orderLanguage)?.languageLabel,
          language,
          apiConfiguration.orderLanguage,
        )} (${apiConfiguration.orderLanguage})`
      }

      const selectedLanguage = effectiveMarket?.languages?.find((language) => language.languageCode === selectedConfiguration.orderLanguage)

      const marketValidPartPeriodStartDate = effectiveMarket?.validPartPeriodStart
        ? periodToDate(effectiveMarket.validPartPeriodStart as PartPeriod)
        : undefined
      const marketValidPartPeriodEndDate = effectiveMarket?.validPartPeriodEnd
        ? periodToDate(effectiveMarket.validPartPeriodEnd as PartPeriod)
        : undefined

      if (
        mergedOverrides.desiredDealerDate?.override &&
        mergedOverrides.desiredDealerDate?.value &&
        mergedOverrides.desiredDealerDate.value !== apiConfiguration.desiredDealerDate
      ) {
        prevValueLabels.desiredDealerDate = isValidDateString(apiConfiguration.desiredDealerDate)
          ? formatDate(i18n, stringToDate(apiConfiguration.desiredDealerDate))
          : '–'
        effectiveConfiguration.desiredDealerDate = mergedOverrides.desiredDealerDate.value
        if (apiConfiguration.partPeriod) {
          effectiveConfiguration.partPeriod = undefined
          selectedConfiguration.partPeriod = undefined
          prevValueLabels.partPeriod = isValidDateString(apiConfiguration.partPeriod)
            ? formatPartPeriod(stringToPeriod(apiConfiguration.partPeriod))
            : '–'
        }
      }

      if ((marketValidPartPeriodStartDate || marketValidPartPeriodEndDate) && effectiveConfiguration.desiredDealerDate) {
        const desiredDealerDate = stringToDate(effectiveConfiguration.desiredDealerDate)
        if (desiredDealerDate) {
          const minMaxDesiredDealerDate = minMaxDate(desiredDealerDate, marketValidPartPeriodStartDate, marketValidPartPeriodEndDate)
          const newDateString = dateToString(minMaxDesiredDealerDate)
          effectiveConfiguration.desiredDealerDate = newDateString
          selectedConfiguration.desiredDealerDate = newDateString
        }
      }

      if (
        mergedOverrides.partPeriod?.override &&
        mergedOverrides.partPeriod?.value &&
        mergedOverrides.partPeriod.value !== apiConfiguration.partPeriod
      ) {
        prevValueLabels.partPeriod = isValidDateString(apiConfiguration.partPeriod)
          ? formatPartPeriod(stringToPeriod(apiConfiguration.partPeriod))
          : '–'
        effectiveConfiguration.partPeriod = mergedOverrides.partPeriod.value
        if (apiConfiguration.desiredDealerDate) {
          effectiveConfiguration.desiredDealerDate = undefined
          selectedConfiguration.desiredDealerDate = undefined
          prevValueLabels.desiredDealerDate = isValidDateString(apiConfiguration.desiredDealerDate)
            ? formatDate(i18n, stringToDate(apiConfiguration.desiredDealerDate))
            : '–'
        }
      }

      if ((marketValidPartPeriodStartDate || marketValidPartPeriodEndDate) && effectiveConfiguration.partPeriod) {
        const partPeriodDate = stringToDate(effectiveConfiguration.partPeriod)
        if (partPeriodDate) {
          const minMaxPartPeriodDate = minMaxDate(partPeriodDate, marketValidPartPeriodStartDate, marketValidPartPeriodEndDate)
          const newPartPeriodString = dateToString(minMaxPartPeriodDate)
          effectiveConfiguration.partPeriod = newPartPeriodString
          selectedConfiguration.partPeriod = newPartPeriodString
        }
      }

      if (calculatedPartPeriod) {
        const calculatedPartPeriodDate = stringToDate(calculatedPartPeriod)
        if (calculatedPartPeriodDate) {
          effectiveConfiguration.partPeriod = dateToString(calculatedPartPeriodDate)
          selectedConfiguration.partPeriod = dateToString(calculatedPartPeriodDate)
        }
      }

      if (mergedOverrides.market?.override && mergedOverrides.market?.value && mergedOverrides.market.value !== apiConfiguration.market) {
        prevValueLabels.market = String(apiConfiguration.market)
        effectiveConfiguration.market = mergedOverrides.market.value
      } else if (overrideTargetMarket && overrideTargetMarket?.marketId !== apiConfiguration.market) {
        prevValueLabels.market = String(apiConfiguration.market)
        effectiveConfiguration.market = overrideTargetMarket.marketId
        selectedConfiguration.market = overrideTargetMarket.marketId
      }

      if (mergedOverrides.language?.override && mergedOverrides.language?.value && mergedOverrides.language.value !== apiConfiguration.language) {
        prevValueLabels.language = String(apiConfiguration.language)
        effectiveConfiguration.language = mergedOverrides.language.value
      } else if (selectedLanguage && baseLanguage !== selectedLanguage) {
        if (selectedLanguage.languageId !== apiConfiguration.language) {
          prevValueLabels.language = String(apiConfiguration.language)
        }
        effectiveConfiguration.language = selectedLanguage.languageId
        selectedConfiguration.language = selectedLanguage.languageId
      }

      if (
        mergedOverrides.vehicleType?.override &&
        mergedOverrides.vehicleType?.value &&
        mergedOverrides.vehicleType.value !== apiConfiguration.vehicleType
      ) {
        prevValueLabels.vehicleType = String(apiConfiguration.vehicleType)
        effectiveConfiguration.vehicleType = mergedOverrides.vehicleType.value
      } else if (overrideTargetMarket && overrideTargetMarket?.vehicleTypeId !== apiConfiguration.vehicleType) {
        prevValueLabels.vehicleType = String(apiConfiguration.vehicleType)
        effectiveConfiguration.vehicleType = overrideTargetMarket.vehicleTypeId
        selectedConfiguration.vehicleType = overrideTargetMarket.vehicleTypeId
      }

      if (mergedOverrides.view?.override && mergedOverrides.view?.value && mergedOverrides.view.value !== apiConfiguration.view) {
        prevValueLabels.view = String(apiConfiguration.view)
        effectiveConfiguration.view = mergedOverrides.view.value
      } else if (overrideTargetMarket && overrideTargetMarket?.viewId !== apiConfiguration.view) {
        prevValueLabels.view = String(apiConfiguration.view)
        effectiveConfiguration.view = overrideTargetMarket.viewId
        selectedConfiguration.view = overrideTargetMarket.viewId
      }

      if (
        mergedOverrides.configurationType?.override &&
        mergedOverrides.configurationType?.value &&
        mergedOverrides.configurationType.value !== apiConfiguration.configurationType
      ) {
        prevValueLabels.configurationType = String(apiConfiguration.configurationType)
        effectiveConfiguration.configurationType = mergedOverrides.configurationType.value
      } else if (overrideTargetMarket && overrideTargetMarket?.configTypeId !== apiConfiguration.configurationType) {
        prevValueLabels.configurationType = String(apiConfiguration.configurationType)
        effectiveConfiguration.configurationType = overrideTargetMarket.configTypeId ?? undefined
        selectedConfiguration.configurationType = overrideTargetMarket.configTypeId ?? undefined
      }

      // the effective language is not available in the effective market
      if (effectiveMarket && !effectiveMarket.languages?.find((language) => language.languageCode === effectiveConfiguration.orderLanguage)) {
        effectiveConfiguration.orderLanguage = selectedConfiguration.orderLanguage
        effectiveConfiguration.language = selectedConfiguration.language
      }

      setInternalState({
        prevValueLabels,
        selectedConfiguration,
        effectiveMarket,
        overrideTargetMarket,
        effectiveConfiguration,
      })

      onOverridesChange?.({
        overrides: mergedOverrides,
        hasOverrides: Object.keys(prevValueLabels).length > 0,
        effectiveConfiguration,
      })
    },
    [apiConfiguration, onOverridesChange, overrides, language, markets, calculatedPartPeriod],
  )

  // NOTE: this will be a problem if another component touches the overrides, and needs to be rethought then
  // biome-ignore lint/correctness/useExhaustiveDependencies: only initially calculate internal state
  useEffect(() => {
    setOverrides(overrides)
  }, [apiConfiguration, calculatedPartPeriod])

  const hasAdvancedChanges =
    prevValueLabels.market || prevValueLabels.language || prevValueLabels.vehicleType || prevValueLabels.view || prevValueLabels.configurationType

  if (marketStatus === 'loading') {
    return (
      <div className="_busy-message">
        <Spinner size="xl" subtitle={t('app.markets.message.loading')} />
      </div>
    )
  }

  const orderCountryFields = (
    <>
      {/* market (orderCountry) */}
      <Checkbox
        label={t('configuration.change-base-params.override-market', 'Market')}
        checked={!!overrides.orderCountry?.override}
        disabled={disabled}
        onChange={(ev) => {
          const override = ev.currentTarget.checked
          setOverrides({
            orderCountry: { ...overrides.orderCountry, override },
          })
        }}
      />
      <Select
        options={marketOptions}
        disabled={disabled || !overrides.orderCountry?.override}
        defaultValue={apiConfiguration.orderCountry}
        onChange={(id) => {
          const selectedMarket = filteredMarkets.find((market) => market.countryCode === id)
          if (selectedMarket && overrides.orderCountry !== selectedMarket.countryCode) {
            // try to preserve the current selected language if available
            const preferredLanguage = selectedMarket.languages?.find((language) => language.languageCode === effectiveConfiguration.orderLanguage)

            console.log(preferredLanguage)
            setOverrides({
              orderCountry: { override: true, value: selectedMarket.countryCode },
            })
          }
        }}
        value={selectedConfiguration.orderCountry}
        slim
      />
      <span className="_prev-value">{prevValueLabels.orderCountry}</span>
    </>
  )

  const orderLanguageFields = (
    <>
      {/* market language (orderLanguage) */}
      <Checkbox
        label={t('configuration.change-base-params.override-market-language', 'Language')}
        checked={!!overrides.orderLanguage?.override}
        disabled={disabled}
        onChange={(ev) => {
          const override = ev.currentTarget.checked
          setOverrides({
            orderLanguage: { ...overrides.orderLanguage, override },
          })
        }}
      />
      <Select
        options={marketLanguageOptions}
        disabled={disabled || !overrides.orderLanguage?.override}
        defaultValue={apiConfiguration.orderLanguage}
        onChange={(id) => {
          const selectedLanguage = effectiveMarket?.languages?.find((language) => language.languageCode === id)
          if (selectedLanguage) {
            setOverrides({
              orderLanguage: { override: true, value: selectedLanguage.languageCode },
            })
          }
        }}
        value={selectedConfiguration.orderLanguage}
        slim
      />
      <span className="_prev-value">{prevValueLabels.orderLanguage}</span>
    </>
  )

  const deliveryDateFields = (
    <>
      {/*  delivery date (desiredDealerDate) */}
      <Checkbox
        label={t('configuration.change-base-params.override-delivery-date', 'Delivery Date')}
        checked={!!overrides.desiredDealerDate?.override}
        disabled={disabled}
        onChange={(ev) => {
          const override = ev.currentTarget.checked
          setOverrides({
            partPeriod: { ...overrides.partPeriod, override: false },
            desiredDealerDate: { ...overrides.desiredDealerDate, override },
          })
        }}
      />
      <TextInput
        type="date"
        value={selectedConfiguration.desiredDealerDate}
        min={effectiveMarket?.validPartPeriodStart ? periodToString(effectiveMarket.validPartPeriodStart as PartPeriod) : undefined}
        max={effectiveMarket?.validPartPeriodEnd ? periodToString(effectiveMarket.validPartPeriodEnd as PartPeriod) : undefined}
        disabled={disabled || !overrides.desiredDealerDate?.override}
        onChange={(x) => {
          if (!Number.isNaN(x.target.valueAsNumber)) {
            const fromValue = dateToString(x.target.valueAsDate)
            setOverrides({
              desiredDealerDate: { override: true, value: fromValue },
            })
          }
        }}
      />
      {prevValueLabels.desiredDealerDate !== selectedConfiguration.desiredDealerDate && (
        <span className="_prev-value">{prevValueLabels.desiredDealerDate}</span>
      )}
    </>
  )

  const prevValuePartPeriod = prevValueLabels.partPeriod ? (prevValueLabels.partPeriod.replaceAll('.', '') as PartPeriod) : undefined
  const configurationPartPeriod = selectedConfiguration.partPeriod ? stringToPeriod(selectedConfiguration.partPeriod) : undefined

  const partPeriodFields = (
    <>
      {/* part period (partPeriod) */}
      <Checkbox
        label={t('configuration.change-base-params.override-part-period', 'Part Period')}
        checked={overrides.partPeriod?.override}
        disabled={disabled}
        onChange={(ev) => {
          const override = ev.currentTarget.checked
          setOverrides({
            partPeriod: { ...overrides.partPeriod, override },
            desiredDealerDate: { ...overrides.desiredDealerDate, override: false },
          })
        }}
      />
      <PartPeriodInput
        value={configurationPartPeriod}
        // TODO add min max when implemented in PartPeriodInput
        disabled={disabled || !overrides.partPeriod?.override}
        onChange={(value) => {
          const stringValue = value ? dateToString(periodToDate(value)) : undefined
          setOverrides({ partPeriod: { override: true, value: stringValue } })
        }}
      />
      {configurationPartPeriod && prevValuePartPeriod !== configurationPartPeriod && <span className="_prev-value">{prevValuePartPeriod}</span>}
    </>
  )

  const marketFields = (
    <>
      {/* Market Id (market) */}
      <Checkbox
        label={t('configuration.change-base-params.override-market-id', 'Market ID')}
        checked={!!overrides.market?.override}
        disabled={disabled}
        onChange={(ev) => {
          const override = ev.currentTarget.checked
          setOverrides({
            market: { ...overrides.market, override },
          })
        }}
      />
      <MarketInput
        marketId={selectedConfiguration.market}
        kind="inline"
        disabled={disabled || !overrides.market?.override}
        onSelected={(id) => setOverrides({ market: { override: true, value: id } })}
      />
      <span className="_prev-value">
        {prevValueLabels.market && <MarketLabel marketId={apiConfiguration.market} />}
        {prevValueLabels.market && ` (${prevValueLabels.market})`}
      </span>
    </>
  )

  const languageFields = (
    <>
      {/* Language Id (language) */}
      <Checkbox
        label={t('configuration.change-base-params.override-language-id', 'Language ID')}
        checked={!!overrides.language?.override}
        disabled={disabled}
        onChange={(ev) => {
          const override = ev.currentTarget.checked
          setOverrides({
            language: { ...overrides.language, override },
          })
        }}
      />
      <LanguageInput
        languageId={selectedConfiguration.language}
        marketId={overrideTargetMarket?.marketId ?? apiConfiguration.market}
        kind="inline"
        disabled={disabled || !overrides.language?.override}
        onSelected={(id) => setOverrides({ language: { override: true, value: id } })}
      />
      <span className="_prev-value">
        {prevValueLabels.language && <LanguageLabel languageId={apiConfiguration.language} marketId={apiConfiguration.market} />}
        {prevValueLabels.language && ` (${prevValueLabels.language})`}
      </span>
    </>
  )

  const vehicleTypeFields = (
    <>
      {/* Vehicle Type Id (vehicleType) */}
      <Checkbox
        label={t('configuration.change-base-params.override-vehicle-type-id', 'Vehicle Type')}
        checked={!!overrides.vehicleType?.override}
        disabled={disabled}
        onChange={(ev) => {
          const override = ev.currentTarget.checked
          setOverrides({
            vehicleType: { ...overrides.vehicleType, override },
          })
        }}
      />
      <VehicleTypeInput
        vehicleTypeId={selectedConfiguration.vehicleType}
        kind="inline"
        disabled={disabled || !overrides.vehicleType?.override}
        onSelected={(id) => setOverrides({ vehicleType: { override: true, value: id } })}
      />
      <span className="_prev-value">
        {prevValueLabels.vehicleType && <VehicleTypeLabel vehicleTypeId={apiConfiguration.vehicleType} />}
        {prevValueLabels.vehicleType && ` (${prevValueLabels.vehicleType})`}
      </span>
    </>
  )

  const viewFields = (
    <>
      {/* View Id (view) */}
      <Checkbox
        label={t('configuration.change-base-params.override-view-id', 'View')}
        checked={!!overrides.view?.override}
        disabled={disabled}
        onChange={(ev) => {
          const override = ev.currentTarget.checked
          setOverrides({
            view: { ...overrides.view, override },
          })
        }}
      />
      <ViewInput
        viewId={selectedConfiguration.view}
        marketId={overrideTargetMarket?.marketId ?? apiConfiguration.market}
        kind="inline"
        disabled={disabled || !overrides.view?.override}
        onSelected={(id) => setOverrides({ view: { override: true, value: id } })}
      />
      <span className="_prev-value">
        {prevValueLabels.view && <ViewLabel viewId={apiConfiguration.view} marketId={apiConfiguration.market} />}
        {prevValueLabels.view && ` (${prevValueLabels.view})`}
      </span>
    </>
  )

  const configurationTypeFields = (
    <>
      {/* Configuration Type Id (configType) */}
      <Checkbox
        label={t('configuration.change-base-params.override-config-type-id', 'Configuration Type')}
        checked={!!overrides.configurationType?.override}
        disabled={disabled}
        onChange={(ev) => {
          const override = ev.currentTarget.checked
          setOverrides({
            configurationType: { ...overrides.configurationType, override },
          })
        }}
      />
      <ConfigurationTypeInput
        configurationTypeId={effectiveConfiguration.configurationType}
        kind="inline"
        disabled={disabled || !overrides.configurationType?.override}
        onSelected={(id) => setOverrides({ configurationType: { override: true, value: id } })}
      />
      <span className="_prev-value">
        {prevValueLabels.configurationType && <ConfigurationTypeLabel configurationTypeId={apiConfiguration.configurationType} />}
        {prevValueLabels.configurationType && ` (${prevValueLabels.configurationType})`}
      </span>
    </>
  )

  const filter = (
    <div className="_usage-filter">
      <label className="_align-right">{t('setup.user-profile')}</label>
      <Select options={usageFilterOptions} value={selectedUsage} onChange={(entry) => setSelectedUsage(entry)} slim />
    </div>
  )

  const contentBlocks =
    displayMode === 'full'
      ? {
          primary: (
            <>
              {filter}
              {orderCountryFields}
              {orderLanguageFields}
              {deliveryDateFields}
              {partPeriodFields}
            </>
          ),
          advanced: (
            <>
              {marketFields}
              {languageFields}
              {vehicleTypeFields}
              {viewFields}
              {configurationTypeFields}
            </>
          ),
        }
      : {
          primary: (
            <>
              {deliveryDateFields}
              {partPeriodFields}
            </>
          ),
          advanced: (
            <>
              {filter}
              {orderCountryFields}
              {orderLanguageFields}
              <hr />
              {marketFields}
              {languageFields}
              {vehicleTypeFields}
              {viewFields}
              {configurationTypeFields}
            </>
          ),
        }

  return (
    <div className={cx(gridClass)}>
      {contentBlocks.primary}

      <Button className="_expander" kind="plain" onClick={() => setIsExpanded(!isExpanded)}>
        <Icon iconUrl={isExpanded ? ChevronDownIcon : ChevronRightIcon} size="xs" />
        {t('configuration.change-base-params.advanced', 'Advanced')}
        {!isExpanded && hasAdvancedChanges && (
          <Icon title={t('configuration.change-base-params.advanced-changes', 'Has changes')} iconUrl={ChangeIcon} size="xs" />
        )}
      </Button>

      {isExpanded && contentBlocks.advanced}
    </div>
  )
}
